import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';

const EditAssignmentModal = ({ isOpen, onClose, assignment, onSuccess }) => {
  const { isDesktop } = useResponsive();

  const [formData, setFormData] = useState({
    due_date: assignment?.due_date || '',
    sets: assignment?.sets || 3,
    reps: assignment?.reps || 10,
    difficulty_level: assignment?.difficulty_level || 'intermediate',
    coach_notes: assignment?.coach_notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate due date (cannot be in past unless assignment is already completed)
      if (formData.due_date && !assignment.completed) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(formData.due_date);

        if (selectedDate < today) {
          throw new Error('Due date cannot be in the past');
        }
      }

      // Validate sets and reps
      if (formData.sets < 1 || formData.reps < 1) {
        throw new Error('Sets and reps must be positive numbers');
      }

      const { error: updateError } = await supabase
        .from('assigned_drills')
        .update({
          due_date: formData.due_date || null,
          sets: formData.sets,
          reps: formData.reps,
          difficulty_level: formData.difficulty_level,
          coach_notes: formData.coach_notes || null,
        })
        .eq('id', assignment.id);

      if (updateError) throw updateError;

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error updating assignment:', err);
      setError(err.message || 'Failed to update assignment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isDesktop ? '20px' : '16px',
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      width: isDesktop ? '600px' : '100%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    header: {
      padding: isDesktop ? '24px' : '16px',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: isDesktop ? '24px' : '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '28px',
      cursor: 'pointer',
      color: '#666',
      padding: '0',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    content: {
      padding: isDesktop ? '24px' : '16px',
    },
    infoSection: {
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      border: '1px solid #e9ecef',
    },
    infoRow: {
      display: 'flex',
      marginBottom: '8px',
      fontSize: '14px',
    },
    infoLabel: {
      fontWeight: '600',
      marginRight: '8px',
      color: '#666',
      minWidth: '100px',
    },
    infoValue: {
      color: '#333',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      minHeight: '44px',
    },
    select: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: '#fff',
      minHeight: '44px',
    },
    textarea: {
      padding: '10px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    numberInputGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
    },
    footer: {
      padding: isDesktop ? '24px' : '16px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    button: {
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
      minWidth: '100px',
    },
    cancelButton: {
      backgroundColor: '#f5f5f5',
      color: '#333',
    },
    submitButton: {
      backgroundColor: '#007bff',
      color: '#fff',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'uppercase',
    },
  };

  const getStatusBadge = () => {
    if (assignment.completed) {
      return (
        <span
          style={{
            ...styles.badge,
            backgroundColor: '#e8f5e9',
            color: '#388e3c',
          }}
        >
          Completed
        </span>
      );
    }

    if (assignment.due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(assignment.due_date);

      if (dueDate < today) {
        return (
          <span
            style={{
              ...styles.badge,
              backgroundColor: '#ffebee',
              color: '#d32f2f',
            }}
          >
            Overdue
          </span>
        );
      }
    }

    return (
      <span
        style={{
          ...styles.badge,
          backgroundColor: '#fff3e0',
          color: '#f57c00',
        }}
      >
        Pending
      </span>
    );
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Edit Assignment</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.infoSection}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Player:</span>
              <span style={styles.infoValue}>{assignment.player?.full_name}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Drill:</span>
              <span style={styles.infoValue}>{assignment.drill?.name}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Assigned:</span>
              <span style={styles.infoValue}>
                {new Date(assignment.assigned_date).toLocaleDateString()}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Status:</span>
              <span style={styles.infoValue}>{getStatusBadge()}</span>
            </div>
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="due_date">
                Due Date
              </label>
              <input
                type="date"
                id="due_date"
                style={styles.input}
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </div>

            <div style={styles.numberInputGroup}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="sets">
                  Sets *
                </label>
                <input
                  type="number"
                  id="sets"
                  style={styles.input}
                  min="1"
                  value={formData.sets}
                  onChange={(e) =>
                    setFormData({ ...formData, sets: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="reps">
                  Reps *
                </label>
                <input
                  type="number"
                  id="reps"
                  style={styles.input}
                  min="1"
                  value={formData.reps}
                  onChange={(e) =>
                    setFormData({ ...formData, reps: parseInt(e.target.value) || 1 })
                  }
                  required
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="difficulty_level">
                Difficulty Level *
              </label>
              <select
                id="difficulty_level"
                style={styles.select}
                value={formData.difficulty_level}
                onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                required
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="coach_notes">
                Coach Notes
              </label>
              <textarea
                id="coach_notes"
                style={styles.textarea}
                value={formData.coach_notes}
                onChange={(e) => setFormData({ ...formData, coach_notes: e.target.value })}
                placeholder="Add notes for the player..."
              />
            </div>
          </form>
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.cancelButton }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{ ...styles.button, ...styles.submitButton }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAssignmentModal;
